import type { KeyboardEvent, ReactNode } from 'react';
import styles from './ListTable.module.css';

/**
 * The simple-list table: one header, the rows in groups drawn as bands ("Screening 9"), each row's name a button that
 * opens it in the panel. ↑ ↓ move between rows, Home and End jump to the ends (06-qol §2); Enter or a click opens.
 * Anything else a row holds (a "Bring back" button, a switch) acts on its own and never opens the row.
 */

export interface ListColumn<TRow> {
  readonly key: string;
  readonly header: string;
  readonly render: (row: TRow) => ReactNode;
  /** Numbers sit right and use the data font. */
  readonly isNumeric?: boolean;
  /** Left out on a phone: the name's second line carries what matters instead. */
  readonly isWideOnly?: boolean;
}

export interface ListGroup<TRow> {
  readonly key: string;
  /** No label: the rows sit under the header with no band. */
  readonly label?: string;
  readonly rows: readonly TRow[];
}

export function ListTable<TRow>({
  label,
  name,
  columns,
  groups,
  idOf,
  selectedId,
  onOpen,
  isMuted,
  isPending,
  phoneLine,
  trailing,
  empty,
}: {
  readonly label: string;
  /** The first column: the row's name and anything beside it (chips). */
  readonly name: { readonly header: string; readonly render: (row: TRow) => ReactNode; readonly label: (row: TRow) => string };
  readonly columns: readonly ListColumn<TRow>[];
  readonly groups: readonly ListGroup<TRow>[];
  readonly idOf: (row: TRow) => string;
  readonly selectedId: string | null;
  /** Left out for a list edited in its own cells (Name matching): the name is then not a button. */
  readonly onOpen?: (row: TRow) => void;
  /** Retired, hidden, archived: drawn quieter. */
  readonly isMuted?: (row: TRow) => boolean;
  /** Still saving: drawn as such and not openable. */
  readonly isPending?: (row: TRow) => boolean;
  /** The name's second line on a phone ("SCR-SAL · used 23×"). */
  readonly phoneLine?: (row: TRow) => ReactNode;
  /** The last cell: a row's own action, such as Bring back. */
  readonly trailing?: (row: TRow) => ReactNode;
  /** Drawn in place of the rows when there are none. */
  readonly empty?: ReactNode;
}): ReactNode {
  const total = groups.reduce((sum, group) => sum + group.rows.length, 0);
  const span = columns.length + 1 + (trailing ? 1 : 0);

  const onKeyDown = (event: KeyboardEvent<HTMLTableElement>): void => {
    if (!(event.target instanceof HTMLElement) || !event.target.matches('[data-row-open]')) return;
    const buttons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[data-row-open]:not([disabled])')];
    const index = buttons.findIndex((button) => button === event.target);
    const target = targetOf(event.key, index, buttons.length);
    if (target === null) return;
    event.preventDefault();
    buttons[target]?.focus();
  };

  return (
    <div className={styles.frame}>
      <table className={styles.table} aria-label={label} onKeyDown={onKeyDown}>
        <thead>
          <tr>
            <th scope="col" className={styles.nameHead}>
              {name.header}
            </th>
            {columns.map((column) => (
              <th key={column.key} scope="col" className={columnClass(column)}>
                {column.header}
              </th>
            ))}
            {trailing && (
              <th scope="col" className={styles.trailing}>
                <span className={styles.hidden}>Actions</span>
              </th>
            )}
          </tr>
        </thead>
        {total === 0 && empty !== undefined ? (
          <tbody>
            <tr>
              <td colSpan={span} className={styles.empty}>
                {empty}
              </td>
            </tr>
          </tbody>
        ) : (
          groups
            .filter((group) => group.rows.length > 0)
            .map((group) => (
              <tbody key={group.key}>
                {group.label !== undefined && (
                  <tr className={styles.band}>
                    <th scope="rowgroup" colSpan={span}>
                      {group.label} <span className={styles.bandCount}>{group.rows.length}</span>
                    </th>
                  </tr>
                )}
                {group.rows.map((row) => {
                  const id = idOf(row);
                  const isOpen = id === selectedId;
                  const isSaving = isPending?.(row) ?? false;
                  return (
                    <tr
                      key={id}
                      className={onOpen ? styles.row : styles.staticRow}
                      aria-current={isOpen || undefined}
                      data-muted={isMuted?.(row) || undefined}
                      data-pending={isSaving || undefined}
                      onClick={onOpen && !isSaving ? () => onOpen(row) : undefined}
                    >
                      <td className={styles.nameCell}>
                        {onOpen ? (
                          <button
                          type="button"
                          className={styles.open}
                          data-row-open
                          disabled={isSaving}
                          aria-label={isSaving ? `${name.label(row)}, saving` : `Open ${name.label(row)}`}
                          aria-pressed={isOpen}
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpen(row);
                          }}
                        >
                          {name.render(row)}
                        </button>
                        ) : (
                          name.render(row)
                        )}
                        {phoneLine && <span className={styles.phoneLine}>{phoneLine(row)}</span>}
                      </td>
                      {columns.map((column) => (
                        <td key={column.key} className={columnClass(column)}>
                          {column.render(row)}
                        </td>
                      ))}
                      {trailing && (
                        <td className={styles.trailing} onClick={(event) => event.stopPropagation()}>
                          {trailing(row)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            ))
        )}
      </table>
    </div>
  );
}

function columnClass<TRow>(column: ListColumn<TRow>): string {
  return [column.isNumeric ? styles.numeric : styles.cell, column.isWideOnly ? styles.wideOnly : ''].filter(Boolean).join(' ');
}

const STEPS: Readonly<Record<string, number>> = { ArrowDown: 1, ArrowUp: -1 };

/** Where ↑ ↓ Home End move from `index` among `count` rows; null for any other key. */
export function targetOf(key: string, index: number, count: number): number | null {
  if (count === 0 || index < 0) return null;
  if (key === 'Home') return 0;
  if (key === 'End') return count - 1;
  const step = STEPS[key];
  if (step === undefined) return null;
  return Math.min(count - 1, Math.max(0, index + step));
}
